/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import moment from 'moment-timezone';
import { ALL_NEWSLETTERS, NEWSLETTER_FILTERS } from 'cio/utils/campaign-newsletter-filters';
import { DEFAULT_FORMAT } from 'cio/utils/broadcasts';
import { CHANNELS } from 'cio/utils/newsletter-channel-info';

import AnalyticsService from 'cio/services/analytics';
import NewsletterModel, { ChannelType } from 'cio/models/newsletter';
import TagModel from 'cio/models/tag';
import { TotalCounts } from 'cio/pods/broadcasts/list/newsletters/index/controller';
import SubscriptionCenterService from 'cio/services/subscription-center';
import ObjectTypesService from 'cio/services/object-types';

const SORT_OPTIONS = [
  {
    label: 'Emails Sent',
    value: 'totalSent',
  },
  {
    label: 'Date Sent',
    value: 'sent',
  },
  {
    label: 'Name',
    value: 'name',
  },
  {
    label: 'Date Created',
    value: 'created',
  },
  {
    label: 'Last Updated',
    value: 'updated',
  },
];

type TimePickerValue = moment.Moment | null;

interface NewslettersFiltersArgs {
  broadcasts: NewsletterModel[];
  channel?: ChannelType;
  endTime?: number;
  searchTagIds: string[];
  sort: string;
  sortDescending: boolean;
  startTime?: number;
  totalCounts: TotalCounts;
  type: string;
  topic: string | null;
  didSetSortDescending: (sortDescending: boolean) => unknown;
  didChangeTags: (tags: TagModel[]) => void;
  didChangeTimePeriod: (options: { startTime: string | null; endTime: string | null }) => unknown;
  didChangeType: () => unknown;
  didChangeTopic: (topic: { label: string; value: string } | null) => unknown;
  selectedObjectTypeIds: string[];
  onObjectTypeIdsChange: (objectTypeIds: string[]) => unknown;
}

export default class NewslettersFilters extends Component<NewslettersFiltersArgs> {
  @service analytics!: AnalyticsService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service objectTypes!: ObjectTypesService;

  sortOptions = SORT_OPTIONS;

  constructor(owner: unknown, args: NewslettersFiltersArgs) {
    super(owner, args);

    this.subscriptionCenter.initializeTask.perform();
  }

  get channelFilterOptions() {
    return [
      {
        label: 'All channels',
        value: 'all',
      },
      ...CHANNELS.map((channel) => {
        return {
          label: channel.info.filterName,
          value: channel.type,
        };
      }),
    ];
  }

  @computed('args.totalCounts')
  get filters() {
    let { totalCounts } = this.args;

    return NEWSLETTER_FILTERS.map((filter) => {
      if (filter.value === ALL_NEWSLETTERS) {
        filter.set('count', totalCounts.sent + totalCounts.drafted + totalCounts.scheduled);
      } else {
        filter.set('count', totalCounts[filter.value as keyof TotalCounts]);
      }

      return filter;
    });
  }
  @computed('filters', 'args.type')
  get currentFilter() {
    return this.filters.findBy('value', this.args.type);
  }
  @computed('args.{startTime,endTime}')
  get selectedRange() {
    let { startTime, endTime } = this.args;
    return {
      start: startTime ? moment(startTime, DEFAULT_FORMAT) : null,
      end: endTime ? moment(endTime, DEFAULT_FORMAT) : null,
    };
  }

  @action
  didChangeTimePeriodAction({ date }: { date: { start: TimePickerValue; end: TimePickerValue } }) {
    if (!date) {
      // neither start or end of date range is selected, or datepicker is cleared - reset time filters:
      return this._clearTimeFilter();
    } else if (date.start && date.end) {
      // both start and end date of range are selected: apply time filter
      this._setTimeFilter(date);
    }
  }

  @action
  didChangeSort(sort: string) {
    this.analytics.track('broadcast', 'list_sorted', { sort });
  }

  private _clearTimeFilter() {
    this.args.didChangeTimePeriod({ startTime: null, endTime: null });
  }

  private _setTimeFilter({ start, end }: { start: TimePickerValue; end: TimePickerValue }) {
    let startTime = start ? moment(start).format(DEFAULT_FORMAT) : null;
    let endTime = end ? moment(end).format(DEFAULT_FORMAT) : null;

    // currently filtering by only start or only end date is not supported: it's UI-only limitation
    // TODO: fix this! it shouldn't be too hard: key issue is datepicker component being re-rendered after picking only start/end date (not full range)
    if (startTime && endTime) {
      this.args.didChangeTimePeriod({ startTime, endTime });
    }
  }
}
